import { useQueryParams } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { AddMiniIcon, ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, ArrowRightIcon } from '@calendly/ui/components/icons';
import { Group, GroupTitle, PopupDropdownMenu } from '@calendly/ui/components/menu';
import { HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { textColorSecondary1 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moveToChangeSubscription } from 'actions/billing/subscription_modal';
import { isAvailableFeatureSelector } from 'features/billing';
import { Option } from 'features/event_type_creation_dropdown';
import { trackEditorOpen } from 'features/event_type_editor';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { useCreateBaseEventType } from './hooks';
const containerCss = "cbpjjo4";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const buttonContainerCss = "b6hiz4";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const styledButtonCss = "s1u3f6o6";
const StyledButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
const noWrapCss = "n1jsz4y8";
const NoWrap = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(noWrapCss, props.className)} />);
const styledPopupDropdownMenuIsTabletDownCss = "s2v8tds";
const styledPopupDropdownMenuCss = "s19tyzo8";
const StyledPopupDropdownMenu = forwardRef((props, ref) => {
  const isTabletDown = useDeviceType('isTabletDown');
  return <PopupDropdownMenu ref={ref} {...props} className={classNames(styledPopupDropdownMenuCss, {
    [styledPopupDropdownMenuIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
const dropdownContainerIsTabletDownCss = "d1vov0q0";
const dropdownContainerCss = "d1f4r62v";
const DropdownContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletDown = useDeviceType('isTabletDown');
  return <Component ref={ref} {...restProps} className={classNames(dropdownContainerCss, {
    [dropdownContainerIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
const eventTypesContainerCss = "eitvthr";
const EventTypesContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(eventTypesContainerCss, props.className)} />;
});
const DropdownContent = ({
  handleOptionClick
}) => <StyledPopupDropdownMenu>
    <Group>
      <GroupTitle>
        <HeadingText variant="eyebrow" color={textColorSecondary1}>
          {I18n.t('publisher.managed_event_types.title')}
        </HeadingText>
      </GroupTitle>
      <EventTypesContainer>
        <Option kind="solo" type="ManagedEventType" name={I18n.t('publisher.event_types.create.one_on_one.name')} description={HtmlParser(I18n.t('publisher.event_types.create.one_on_one.description'), {
        ArrowRightIcon
      })} example={I18n.t('publisher.event_types.create.one_on_one.example')} onClick={() => handleOptionClick('solo')} />
        <Option kind="group" type="ManagedEventType" name={I18n.t('publisher.event_types.create.group.name')} description={HtmlParser(I18n.t('publisher.event_types.create.group.description'), {
        ArrowRightIcon
      })} example={I18n.t('publisher.event_types.create.group.example')} onClick={() => handleOptionClick('group')} />
      </EventTypesContainer>
    </Group>
  </StyledPopupDropdownMenu>;
export const CreateDropdownButton = ({
  disabled
}) => {
  const dispatch = useDispatch();
  const track = useTrack();
  const {
    new: isOpenByDefault
  } = useQueryParams();
  const [toggle, Dropdown, isOpen] = useDropdown(Boolean(isOpenByDefault));
  const {
    UserGroupPopup,
    handleDifferentUserTypeFlows
  } = useCreateBaseEventType();
  const areManagedEventsAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'managed_events'
  }));
  const trackCreateMETV3 = ({
    kind
  }) => {
    const etKind = kind || 'solo';
    trackEditorOpen({
      track,
      destination: 'create',
      source: 'admin center managed et create',
      kind: etKind,
      type: 'ManagedEventType'
    });
  };
  const handleClick = kind => {
    const createNewMET = () => {
      handleDifferentUserTypeFlows(kind);
      trackCreateMETV3({
        kind
      });
    };
    if (areManagedEventsAvailable) {
      createNewMET();
    } else {
      dispatch(moveToChangeSubscription({
        featureKey: 'managed_events',
        preface: true,
        onSuccess: async () => {
          createNewMET();
        }
      }));
    }
  };
  return <>
      <Container>
        <ButtonContainer>
          <StyledButton size="small" onClick={toggle} aria-expanded={isOpen} data-id="managed-events-create-button" disabled={disabled}>
            <AddMiniIcon size="large" />
            <NoWrap>{I18n.t('publisher.action.create')}</NoWrap>
            {isOpen ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
          </StyledButton>
        </ButtonContainer>
        <DropdownContainer>
          <Dropdown>
            <DropdownContent handleOptionClick={handleClick} />
          </Dropdown>
        </DropdownContainer>
      </Container>
      {UserGroupPopup}
    </>;
};

require("./create_dropdown_button.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./create_dropdown_button.js");